/**
 * The client half of the parse-activity stream: a pure fold over the events a
 * parse job's watch sends, and the hook that owns the sockets those events
 * arrive on.
 *
 * Two things live here, deliberately separable:
 *
 *  - `foldParseEvent(activity, event)` is a **pure function**. No timers, no
 *    fake clocks, no interpolation, no network, no React. Everything the panel
 *    shows is derived from events that actually happened, which is what makes
 *    the view testable and what retired the previous design's synthetic,
 *    time-driven step advancement. Stage 6 mounts this same fold for Genie, so
 *    it must stay free of anything composer-specific: it transcribes the event
 *    vocabulary and maps nothing to English. Route keys, stage names and tool
 *    names come out raw; turning `checkin_feature_extraction_places` into
 *    "Places" is the panel's job.
 *
 *  - `useParseActivity()` owns the socket lifetime, and the records the panels
 *    render. Per **parse**, not per composer and not per page: open on
 *    commit-to-parse, close on the result or a terminal error,
 *    close-and-reopen when a newer parse supersedes an older
 *    one, and close everything on unmount. That bounds open sockets by
 *    concurrent parses rather than by open tabs — and socket leaks are the new
 *    failure mode this whole transport introduces, since nothing else in the
 *    app opens a socket per operation.
 *
 * It also owns `reviewParse`, which reads a *finished* parse back out of
 * `parse_activity_events` and puts it on the row. That path shares everything
 * that matters with the live one: the same events, folded by the same
 * `foldParseEvents`, into the same record type. Only the lifetime differs — the
 * socket streams a parse as it happens, the store answers for one that is over
 * (or one running somewhere this browser has no connection to, which is what a
 * parse run as a background job looks like from here). A second fold, or a
 * second panel, would be the signal that this went wrong.
 *
 * And `watchJob`, the live half for a job: the job's watch socket streams its
 * stored record as it is written, through the same tracker `beginParse` hands
 * out — so the same generation guard, the same socket map and the same unmount
 * cleanup, and the same fold into the same record. `reviewJob` is its stored
 * half, the job's `reviewParse`.
 *
 * The fold tolerates a stream that is incomplete or out of order, because the
 * server is allowed to drop events (a slow reader must never apply back-pressure
 * to a parse) and a socket can close mid-run. An event for a step it has not
 * seen creates that step rather than being discarded; a resolved `tool_call`
 * with no `running` half is appended as-is.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchJobActivity, fetchParseActivity, stageAFromEvent, watchJobActivity } from '../api/client'
import type { JobResultReady, ParseActivityEvent, StoredJobActivity } from '../api/client'
import { getTechnicalTier, setTechnicalTier } from './useTechnicalTier'

type ParseStepStatus = 'pending' | 'active' | 'done' | 'error'
/**
 * Which lock a skipped row stands for, and so which sentence the panel writes
 * for it: this feature's value was edited by hand, the whole project was, or
 * the row is the collapsed one standing for every locked scalar feature.
 */
type ParseSkipScope = 'feature' | 'project' | 'scalars'
type ParseToolStatus = 'running' | 'ok' | 'error'
type ParseRunStatus = 'running' | 'ok' | 'error' | 'loop_exhausted'

export interface ParseToolCall {
    /** The server's `call_id`; the `running` and resolved events share it. */
    callId: string
    tool: string
    args: Record<string, unknown>
    /**
     * How many entries each argument list lost to the server's clipping, keyed
     * by the argument it belongs to — `{queries: 2}`.
     *
     * Beside the arguments and never inside them. The server used to append
     * `"… +2 more"` to a clipped list, which everything downstream read as one
     * more of whatever the list held: the panel quoted it and drew it as a
     * person (reported 2026-09-23). Absent whenever nothing was clipped.
     */
    argsOmitted?: Record<string, number>
    status: ParseToolStatus
    /** A short derived string — never the raw tool payload. */
    resultSummary?: string
    /**
     * The result's items, named — the list the summary is a sentence about.
     * Bounded by the server; `resultTotal` is the true count, so the technical
     * tier can name a prefix and say how much it is not naming. Never the raw
     * payload: each entry is the same derived display string the summary uses.
     */
    resultItems?: string[]
    /** How many items the result held, whatever `resultItems` names. */
    resultTotal?: number
    durationMs?: number
    /** Milliseconds since the sink was armed, off the server's monotonic clock. */
    at: number
}

/**
 * How a named value reached the model, and so which designation the drawer
 * closes its group with.
 *
 * `roster` is an item the prompt already carried — the recency/frequency slice
 * of the user's own collections this product inlines — and `lookup` is one it
 * did not, which the model can only have seen in a tool result. The server
 * derives it from which list an id was in; it is a fact about our prompt, and
 * the only place it can be known is the parser's own call site
 * (`docs/parse_activity.md` §5).
 */
export type ParseValueVia = 'roster' | 'lookup'

/**
 * One value a step named, and how it was found.
 *
 * `via` is absent for everything that was not found at all: an item the parse
 * *created*, a scalar read out of the note's own words, and every value in an
 * event written before the designation existed — `parse_activity_events` holds
 * those, and the fold reads them as plain strings rather than rejecting them.
 */
export interface ParseStepValue {
    name: string
    via?: ParseValueVia
}

export interface ParseStep {
    /**
     * Stable within a run, and **unique per piece of work**: `stage:<name>`, or
     * `loop:<loop id>` for a loop the server gave an identity to (falling back
     * to `loop:<route key>` for one that did not).
     *
     * Not the route key, because a route key is not an identity: the split
     * parser runs one loop per reference feature and two of them regularly
     * route the same — the global People feature and a project's own
     * people-reference feature are both `checkin_feature_extraction_people`.
     * Keyed on the route key those collapsed into one row whose count was
     * whichever loop finished last, which is a row that is quietly wrong
     * rather than merely unhelpful (reported 2026-09-20).
     */
    key: string
    /**
     * `stage:<name>` / `loop:<route key>` — what the panel's label tables are
     * keyed by. Separate from `key` because two rows may legitimately share it.
     */
    phraseKey: string
    /**
     * `stage` and `loop` are work that ran. `skipped` is work the parse was
     * **told not to do** — a feature or a project the user has edited by hand
     * on this check-in, which is dropped before the extract stage fans out and
     * so emits no loop events at all.
     *
     * A kind rather than a fifth `status`: status answers "how far did this
     * work get", and skipped work never started, so it has no position on that
     * axis. The panel short-circuits on this kind before any tense or metric
     * logic runs, which is what makes it structurally incapable of claiming a
     * duration, a model or `0 iters` for a row that has none.
     */
    kind: 'stage' | 'loop' | 'skipped'
    /** Which lock a `skipped` row stands for. Absent on every other kind. */
    skip?: ParseSkipScope
    /** `assistant` when Genie holds that lock; absent when the user does. */
    skipOwner?: 'assistant'
    /** The server's own label, or the raw key when it sent none. Not English. */
    label: string
    /**
     * What this loop was resolving, in the user's own words — the feature's
     * title. Absent for a loop with nothing to name (the scalar batch, Genie's
     * own loops), and those rows read exactly as they did before.
     */
    subject?: string
    status: ParseStepStatus
    model?: string
    effort?: string
    iterations: number
    promptTokens: number
    cachedTokens: number
    completionTokens: number
    /**
     * How many values the loop's final plan emitted, once it finished.
     *
     * Always the true count, and never derived from `values` below: a loop
     * that detected more than an event may carry names a prefix of them, and
     * the difference is what the panel reports as `+N more`.
     */
    emitted?: number
    /**
     * Those values, named — what the step *concluded*.
     *
     * They arrive as their own `loop_values` event rather than on
     * `loop_finished`, because naming them needs the roster and a database
     * read and so happens in the parser rather than in the loop
     * (`docs/parse_activity.md` §1). A loop whose caller names nothing —
     * Genie's — keeps this empty and reports its count alone.
     *
     * Each carries **how it was found** in `via`, which the drawer turns into
     * a designation. On a `skipped` row this holds what one collapsed row
     * stands for — the titles of the locked scalar features — behind the same
     * drawer, with `emitted` as their true count, and nothing there was found
     * at all, so nothing there is designated.
     */
    values: ParseStepValue[]
    durationMs?: number
    tools: ParseToolCall[]
    at: number
}

/**
 * One collection item the parse created, rather than matched.
 *
 * `kind` is the collection's **system kind** (`people`, `places`, or `''`),
 * which is what the panel picks its noun from. The collection's *name* is a
 * fallback for everything else and a thing the user can rename, so a panel
 * that chose "person" by matching the string "People" would break the day
 * they did.
 */
export interface ParseCreatedItem {
    collection: string
    kind: string
    value: string
}

/** One act a Genie turn performed — a write its control path dispatched
 *  directly, which no tool loop can report. */
export interface ParseWriteAction {
    tool: string
    subject?: string
}

export interface ParseActivity {
    startedAt: number
    /**
     * The newest event's `at` — milliseconds since the sink was armed, off the
     * server's monotonic clock.
     *
     * This is how long the run took, and it is the only honest answer for a
     * record read back out of the store: `startedAt` on one of those is when
     * the *reader* opened it, so a wall clock would count the wrong thing from
     * the wrong moment. While a parse is live the panel ticks its own wall
     * clock instead, because events arrive in bursts and a timer that only
     * moves when one lands is not a timer.
     */
    lastAt: number
    /** Steps in start order — the order the events arrived in. */
    steps: ParseStep[]
    /**
     * Collection items this parse **created**, in the order they were made.
     *
     * The one outcome the panel reports, because it is the one the product
     * cannot: the chip under the note looks identical whether the item behind
     * it was matched or invented. What the parse *saved* had a row here too
     * and was retired in 2026-09-21 — the chips are the saved values, rendered
     * better than a sentence can, and a row restating them was the panel
     * competing with the product.
     *
     * One entry per item; the panel groups them into one row per kind.
     */
    created: ParseCreatedItem[]
    /**
     * Writes a Genie turn performed. Its control path dispatches
     * `create_project`, `commit_draft` and the rest directly rather than
     * through a tool loop, so without this a turn that created a project drew
     * the moderator's silence where its one consequential act was.
     */
    writes: ParseWriteAction[]
    /** Derived from the newest event. Raw; the panel translates it. */
    currentLabel: string
    /**
     * Stage A's flavor words for the collapsed line ("Dancing", "Cooking"),
     * or empty. Not folded from an event and deliberately so: they arrive on
     * the socket's `stage_a` control message, they are an *answer* rather than
     * something that happened, and the fold's whole discipline is that it
     * transcribes events and knows no English. A surface with no Stage A —
     * Genie's own turn — never sets them and reads exactly as it did before.
     */
    statusWords: string[]
    expanded: boolean
    /**
     * **This panel's** tier. Seeded from the sticky "Details" preference
     * (`hooks/useTechnicalTier`) when the record is created, and moved after
     * that only by this record's own toggle — two analyses can be open at once
     * and reading the call tree of one must not switch the other (reported
     * 2026-09-23). It lives on the record so the panel renders from its props
     * alone, and so a record built by hand in a test draws the tier it says it
     * draws.
     */
    technical: boolean
    /** Events the server refused past its cap, or could not send. */
    dropped: number
    status: ParseRunStatus
    /**
     * True when this record was read back out of the store rather than streamed
     * — the reader deliberately opened a parse that is over. It changes exactly
     * two things: the collapsed line reads as a record rather than as work in
     * progress, and the page keeps the panel mounted on a row whose indicator
     * is not up. The steps, the tool calls and both tiers are identical, because
     * they are the same events through the same fold.
     */
    review: boolean
}

export const emptyParseActivity = (startedAt: number = Date.now()): ParseActivity => ({
    startedAt,
    lastAt: 0,
    steps: [],
    created: [],
    writes: [],
    currentLabel: '',
    statusWords: [],
    expanded: false,
    technical: false,
    dropped: 0,
    status: 'running',
    review: false,
})

const STAGE_KEY = (stage: string) => `stage:${stage}`
const LOOP_KEY = (loopKey: string) => `loop:${loopKey}`

const newStep = (
    key: string,
    phraseKey: string,
    kind: ParseStep['kind'],
    label: string,
    at: number,
): ParseStep => ({
    key,
    phraseKey,
    kind,
    label,
    status: 'active',
    iterations: 0,
    promptTokens: 0,
    cachedTokens: 0,
    completionTokens: 0,
    values: [],
    tools: [],
    at,
})

const str = (value: unknown, fallback = ''): string =>
    typeof value === 'string' ? value : fallback

const VIA_VALUES: ParseValueVia[] = ['roster', 'lookup']

/**
 * A step's named values, or `undefined` for anything that is not a list.
 *
 * **Read-time forgiveness against write-time validation** — the pattern
 * `docs/llm_routing_and_limits.md` describes, and the reason it applies here is
 * that `parse_activity_events` outlives any one shape. An entry may be
 * `{name, via}`, which is what the server writes today, or a bare string, which
 * is every row written before the designation existed and what `loop_skipped`
 * still sends: a skipped feature was not found, so it has nothing to designate.
 * A designation the client does not recognize is dropped rather than shown, so
 * an unknown token reads as undesignated instead of leaking a wire word into
 * the sentence.
 */
const valueList = (value: unknown): ParseStepValue[] | undefined => {
    if (!Array.isArray(value)) return undefined
    const out: ParseStepValue[] = []
    for (const entry of value) {
        if (typeof entry === 'string') {
            out.push({ name: entry })
            continue
        }
        if (!entry || typeof entry !== 'object') continue
        const name = (entry as { name?: unknown }).name
        if (typeof name !== 'string') continue
        const via = (entry as { via?: unknown }).via
        out.push(
            typeof via === 'string' && (VIA_VALUES as string[]).includes(via)
                ? { name, via: via as ParseValueVia }
                : { name },
        )
    }
    return out
}

const num =(value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined

/** The strings of a list-valued event field, or `undefined` for anything else.
 *  Non-strings are dropped rather than coerced — a number in a list of names is
 *  the id bug this panel has already shipped twice. */
const stringList = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) return undefined
    return value.filter((entry): entry is string => typeof entry === 'string')
}

/** `{queries: 2}` — how much of each argument the server clipped away. Only
 *  finite positive numbers survive, so a malformed field reads as "nothing was
 *  clipped" rather than as a negative tail on a sentence. */
const countMap = (value: unknown): Record<string, number> | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
    const out: Record<string, number> = {}
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
        const n = num(raw)
        if (n !== undefined && n > 0) out[key] = n
    }
    return Object.keys(out).length ? out : undefined
}

/**
 * The shape a clipped list carried until 2026-09-23: the overflow written into
 * the list as its last entry, `"… +2 more"`.
 */
const LEGACY_OVERFLOW = /^…\s*\+(\d+)\s+more$/

/**
 * A call's arguments, with any legacy overflow entry lifted out of the lists
 * and counted beside them.
 *
 * **Read-time forgiveness against write-time validation** — the same pattern
 * `valueList` applies to a pre-designation value, and it applies here for the
 * same reason: `parse_activity_events` outlives any one wire shape, and every
 * parse stored before 2026-09-23 holds its overflow inside the list. Left
 * there, a reader opening one of those records sees `“… +2 more”` drawn as the
 * eleventh person the step looked up, which is the bug itself, read back.
 */
const callArgs = (
    raw: unknown,
    omitted: Record<string, number> | undefined,
): { args: Record<string, unknown>; argsOmitted?: Record<string, number> } => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return { args: {}, argsOmitted: omitted }
    }
    const args: Record<string, unknown> = {}
    const counts: Record<string, number> = { ...(omitted || {}) }
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        if (!Array.isArray(value) || !value.length) {
            args[key] = value
            continue
        }
        const last = value[value.length - 1]
        const match = typeof last === 'string' ? LEGACY_OVERFLOW.exec(last.trim()) : null
        if (!match) {
            args[key] = value
            continue
        }
        args[key] = value.slice(0, -1)
        counts[key] = (counts[key] ?? 0) + Number(match[1])
    }
    return {
        args,
        argsOmitted: Object.keys(counts).length ? counts : undefined,
    }
}

/**
 * Replace the step at `key`, or append a fresh one when the stream never
 * announced it. Appending rather than dropping is what keeps a capped or
 * reordered stream readable instead of silently short.
 */
const upsertStep = (
    activity: ParseActivity,
    key: string,
    phraseKey: string,
    kind: ParseStep['kind'],
    label: string,
    at: number,
    update: (step: ParseStep) => ParseStep,
): ParseStep[] => {
    const index = activity.steps.findIndex((s) => s.key === key)
    if (index === -1) {
        return [...activity.steps, update(newStep(key, phraseKey, kind, label, at))]
    }
    const next = [...activity.steps]
    next[index] = update(next[index])
    return next
}

/**
 * The two keys a loop event carries: the row it belongs to, and the phrases
 * that row reads with.
 *
 * `loop_id` is the identity and `loop_key` the route; an event without a
 * `loop_id` (an older caller, a server that did not stamp one) falls back to
 * the route key, which is exactly the behaviour that existed before — one row
 * per route.
 */
const loopKeys = (event: ParseActivityEvent): { key: string; phraseKey: string } => {
    const route = str(event.loop_key, 'loop')
    const id = str(event.loop_id)
    return { key: LOOP_KEY(id || route), phraseKey: LOOP_KEY(route) }
}

/**
 * One event, folded into the running record. Pure: the input is never mutated
 * and an unrecognized event type returns the record unchanged.
 */
export const foldParseEvent = (
    activity: ParseActivity,
    event: ParseActivityEvent,
): ParseActivity => {
    if (!event || typeof event.type !== 'string') return activity
    const next = foldOne(activity, event)
    // Stamped here rather than in each of the switch's arms: `lastAt` is a
    // property of the record, not of any one kind of event, and an arm that
    // forgot it would stop the clock on a run that was still going. An event
    // the fold ignored leaves the record alone, clock included.
    if (next === activity) return activity
    const at = num(event.at) ?? 0
    return at > next.lastAt ? { ...next, lastAt: at } : next
}

const foldOne = (activity: ParseActivity, event: ParseActivityEvent): ParseActivity => {
    const at = num(event.at) ?? 0

    switch (event.type) {
        case 'stage_started': {
            const stage = str(event.stage, 'stage')
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    STAGE_KEY(stage),
                    STAGE_KEY(stage),
                    'stage',
                    stage,
                    at,
                    (step) => ({ ...step, status: 'active' }),
                ),
                currentLabel: stage,
            }
        }

        case 'stage_finished': {
            const stage = str(event.stage, 'stage')
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    STAGE_KEY(stage),
                    STAGE_KEY(stage),
                    'stage',
                    stage,
                    at,
                    (step) => ({
                        ...step,
                        // A stage that already failed keeps saying so; the pair
                        // is emitted from a `finally`, so it fires on the way
                        // out of a raise too.
                        status: step.status === 'error' ? 'error' : 'done',
                        durationMs: num(event.duration_ms) ?? step.durationMs,
                    }),
                ),
            }
        }

        case 'loop_started': {
            const { key, phraseKey } = loopKeys(event)
            const label = str(event.label) || str(event.loop_key, 'loop')
            const subject = str(event.subject) || undefined
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    key,
                    phraseKey,
                    'loop',
                    label,
                    at,
                    (step) => ({
                        ...step,
                        label,
                        subject: subject ?? step.subject,
                        status: 'active',
                        model: str(event.model) || step.model,
                        effort: str(event.effort) || step.effort,
                    }),
                ),
                currentLabel: label,
            }
        }

        case 'iteration': {
            const { key, phraseKey } = loopKeys(event)
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    key,
                    phraseKey,
                    'loop',
                    str(event.loop_key, 'loop'),
                    at,
                    (step) => ({
                        ...step,
                        subject: str(event.subject) || step.subject,
                        // `n` rather than a local counter: the fallback
                        // iteration the loop runs after its cap reports
                        // `max_iterations + 1`, and that is the honest number.
                        iterations: Math.max(step.iterations, num(event.n) ?? step.iterations + 1),
                        promptTokens: step.promptTokens + (num(event.prompt_tokens) ?? 0),
                        cachedTokens: step.cachedTokens + (num(event.cached_tokens) ?? 0),
                        completionTokens:
                            step.completionTokens + (num(event.completion_tokens) ?? 0),
                    }),
                ),
            }
        }

        case 'tool_call': {
            const { key, phraseKey } = loopKeys(event)
            const callId = str(event.call_id) || `${key}:${event.seq}`
            const tool = str(event.tool, 'tool')
            const status = str(event.status, 'running') as ParseToolStatus
            const { args, argsOmitted } = callArgs(
                event.args,
                countMap(event.args_omitted),
            )
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    key,
                    phraseKey,
                    'loop',
                    str(event.loop_key, 'loop'),
                    at,
                    (step) => {
                        const index = step.tools.findIndex((t) => t.callId === callId)
                        const resolved: ParseToolCall = {
                            callId,
                            tool,
                            args,
                            argsOmitted,
                            status,
                            resultSummary: str(event.result_summary) || undefined,
                            resultItems: stringList(event.result_items),
                            resultTotal: num(event.result_total),
                            durationMs: num(event.duration_ms),
                            at: index === -1 ? at : step.tools[index].at,
                        }
                        const subject = str(event.subject) || step.subject
                        if (index === -1) {
                            return { ...step, subject, tools: [...step.tools, resolved] }
                        }
                        const tools = [...step.tools]
                        // The resolved half fills the running one in place, so
                        // the row the reader is already looking at gains its
                        // result rather than a second row appearing beside it.
                        tools[index] = { ...tools[index], ...resolved }
                        return { ...step, subject, tools }
                    },
                ),
                currentLabel: status === 'running' ? tool : activity.currentLabel,
            }
        }

        case 'loop_finished': {
            const { key, phraseKey } = loopKeys(event)
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    key,
                    phraseKey,
                    'loop',
                    str(event.loop_key, 'loop'),
                    at,
                    (step) => ({
                        ...step,
                        subject: str(event.subject) || step.subject,
                        status: 'done',
                        emitted: num(event.emitted) ?? step.emitted,
                        durationMs: num(event.duration_ms) ?? step.durationMs,
                    }),
                ),
            }
        }

        // What a finished loop detected, named. Its own event because the
        // names are the parser's to produce, not the loop's: a plan holds a
        // matched reference as an id, and turning that id into "Alina Petrova
        // (friend)" needs the roster and a database read
        // (`docs/parse_activity.md` §1, §5). It carries the loop's id, so it
        // merges into the row that loop's own events opened.
        case 'loop_values': {
            const { key, phraseKey } = loopKeys(event)
            const values = valueList(event.values)
            if (!values) return activity
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    key,
                    phraseKey,
                    'loop',
                    str(event.loop_key, 'loop'),
                    at,
                    // Replaced, not appended: a loop that names its values
                    // twice — the completeness critic re-emitting a completer
                    // set under one id — is correcting itself, and the latest
                    // naming is the one that matches the count beside it.
                    (step) => ({ ...step, values }),
                ),
            }
        }

        // What a stage concluded, named — `loop_values` for work that is not a
        // loop. Stage A runs no tool loop, so its row had no count and no
        // names to open: the one row in the panel that reported only that it
        // had happened. Keyed by stage rather than by loop id, which is the
        // only identity a stage has.
        case 'stage_values': {
            const stage = str(event.stage)
            const values = valueList(event.values)
            if (!stage || !values || !values.length) return activity
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    STAGE_KEY(stage),
                    STAGE_KEY(stage),
                    'stage',
                    stage,
                    at,
                    (step) => ({
                        ...step,
                        values,
                        emitted: num(event.emitted) ?? values.length,
                    }),
                ),
            }
        }

        // Work the parse was told to leave alone. Its own event and its own
        // kind, because a locked feature is dropped before the extract stage
        // fans out: it has no loop, no route key, no duration and nothing to
        // report a status about. The parser emits it for the same reason it
        // emits `loop_values` — the fact lives with the caller, and
        // `run_tool_loop` must gain no knowledge of locks
        // (`docs/parse_activity.md` §5).
        case 'loop_skipped': {
            const raw = str(event.scope)
            const skipId = str(event.skip_id)
            if (raw !== 'feature' && raw !== 'project' && raw !== 'scalars') {
                return activity
            }
            const scope: ParseSkipScope = raw
            if (!skipId) return activity
            const subject = str(event.subject) || undefined
            const values = valueList(event.values) ?? []
            // A row that cannot say what it skipped is a blank row, and the
            // panel is better off one row shorter. A feature or project row
            // says it with its title; the collapsed scalar row has no title of
            // its own and says it with its list.
            if (scope === 'scalars' ? !values.length : !subject) return activity
            return {
                ...activity,
                steps: upsertStep(
                    activity,
                    `skip:${skipId}`,
                    `skip:${scope}`,
                    'skipped',
                    subject || scope,
                    at,
                    (step) => ({
                        ...step,
                        kind: 'skipped',
                        skip: scope,
                        ...(event.owner === 'assistant' ? { skipOwner: 'assistant' as const } : {}),
                        subject: subject ?? step.subject,
                        values: values.length ? values : step.values,
                        emitted: num(event.skipped) ?? step.emitted,
                        // Never started, so it never gets anywhere: `pending`
                        // is the only one of the four that is true, and
                        // `parse_finished` below leaves this kind alone rather
                        // than resolving it the way it resolves work in
                        // flight.
                        status: 'pending',
                    }),
                ),
            }
        }

        // The funnel's own events. They describe an *act* rather than a step,
        // so neither touches `steps` and neither has a loop to belong to: a
        // write is not a loop fact and cannot be derived from one. See
        // `docs/parse_activity.md` §5.
        //
        // One event per created item, transcribed one for one. Grouping them
        // into "Found new people: A, B" is the panel's job, because that is
        // where English lives and because an emit site knows about exactly one
        // insert.
        case 'item_created': {
            const value = str(event.value)
            if (!value) return activity
            return {
                ...activity,
                created: [
                    ...activity.created,
                    {
                        collection: str(event.collection),
                        kind: str(event.kind),
                        value,
                    },
                ],
            }
        }

        case 'write_dispatch': {
            const tool = str(event.tool)
            if (!tool) return activity
            const subject = str(event.subject) || undefined
            return { ...activity, writes: [...activity.writes, { tool, subject }] }
        }

        case 'parse_finished': {
            const status = str(event.status, 'ok') as ParseRunStatus
            const ok = status === 'ok'
            return {
                ...activity,
                status,
                dropped: num(event.dropped) ?? activity.dropped,
                // Whatever was still in flight ends here. On a failure the
                // steps that never finished are the ones that failed, which is
                // what leaves the failing step marked and the rest untouched.
                //
                // A skip was never in flight, so it ends nowhere: resolving it
                // to `done` would be the panel claiming work it was explicitly
                // told not to do, and to `error` that a lock is a failure.
                steps: activity.steps.map((step) =>
                    step.kind !== 'skipped' &&
                    (step.status === 'active' || step.status === 'pending')
                        ? { ...step, status: ok ? 'done' : 'error' }
                        : step,
                ),
            }
        }

        default:
            return activity
    }
}

/** Fold a whole batch, for a snapshot that arrives at once. */
export const foldParseEvents = (
    activity: ParseActivity,
    events: ParseActivityEvent[],
): ParseActivity => events.reduce(foldParseEvent, activity)

/**
 * `close()` on a socket that never opened is a no-op in the spec but throws in
 * some environments; and a socket still CONNECTING closes cleanly. Neither is
 * worth a failure at this layer.
 */
const closeQuietly = (socket: WebSocket | null | undefined) => {
    if (!socket) return
    try {
        socket.close()
    } catch {
        /* already closed, or never opened */
    }
}

export interface ParseTracker {
    /** Feed one event into this parse's record. Pass to `parseCheckinViaJob`. */
    onActivity: (event: ParseActivityEvent) => void
    /**
     * Stage A's flavor words, from the parse's `stage_a` event. Goes
     * through the tracker rather than straight into the record so a superseded
     * parse's words cannot land on the newer parse's panel — the same
     * generation guard `onActivity` and `onSocket` answer to.
     */
    onStatusWords: (words: string[]) => void
    /** Hand the hook the watch socket to own. Pass to `parseCheckinViaJob`. */
    onSocket: (socket: WebSocket) => void
    /** The parse ended (either way): close the socket, keep the record. */
    end: () => void
    /** Superseded or abandoned: close the socket and drop the record. */
    cancel: () => void
}

export interface UseParseActivityResult {
    activities: Record<number, ParseActivity>
    /**
     * Claim the indicator for one parse of `checkinId`.
     *
     * Closes whatever socket the previous call for this checkin opened, which
     * is the close-and-reopen the seq guard in `runAIParse` asks for: that
     * guard already decides which invocation owns the indicator, so it now
     * decides which owns a socket too — one rule rather than two.
     */
    beginParse: (checkinId: number) => ParseTracker
    /**
     * Watch a background job live, folding its events into the record under
     * `recordKey` — a check-in id when the job is about one, or a sentinel key
     * (as Genie's surface uses) when nothing stands behind it.
     *
     * A `beginParse` for that key underneath, so a newer parse or watch
     * supersedes this one, and unmount closes it. Resolves on `result_ready`;
     * rejects with `ParseQuotaError` / `ParseSocketError` like a parse does,
     * including `closed` when the watch ends early — the job runs on.
     */
    watchJob: (recordKey: number, jobId: string, scope?: string) => Promise<JobResultReady>
    /**
     * Put a job's stored record under `recordKey`, folded from the table — the
     * job's `reviewParse`. Resolves with what the read returned, so a caller
     * can tell a record that is still open (`status: 'running'`) from one that
     * is closed; resolves null without reading while a watch still writes into
     * that key, since the live record is the better answer. A job with no
     * events yet takes any earlier record off the key.
     */
    reviewJob: (
        recordKey: number,
        jobId: string,
        scope?: string,
    ) => Promise<StoredJobActivity | null>
    /**
     * Put the check-in's stored parse on its row, folded from the table.
     * Resolves false when there is nothing stored — a check-in parsed before
     * this existed, or one never parsed at all — so the caller can say so
     * rather than opening an empty panel.
     */
    reviewParse: (checkinId: number) => Promise<boolean>
    /** Take a reviewed record back off the row. Live records are left alone. */
    clearReview: (checkinId: number) => void
    setExpanded: (checkinId: number, expanded: boolean) => void
    setTechnical: (checkinId: number, technical: boolean) => void
}

export const useParseActivity = (): UseParseActivityResult => {
    const [activities, setActivities] = useState<Record<number, ParseActivity>>({})
    // Not state: nothing renders from it, and a render must never be what a
    // socket's lifetime depends on.
    const socketsRef = useRef<Map<number, WebSocket>>(new Map())
    // Which invocation currently owns a checkin's record and socket. The same
    // idea as `runAIParse`'s seq guard, and for the same reason: the older
    // invocation has no way to know it was superseded except by asking.
    const genRef = useRef<Map<number, number>>(new Map())

    const clearSocket = useCallback((checkinId: number) => {
        const socket = socketsRef.current.get(checkinId)
        if (socket) {
            socketsRef.current.delete(checkinId)
            closeQuietly(socket)
        }
    }, [])

    const beginParse = useCallback(
        (checkinId: number): ParseTracker => {
            clearSocket(checkinId)
            setActivities((prev) => ({
                ...prev,
                // Seeded from the preference rather than left false for the
                // mirror below to fix up: a parse that opens already-technical
                // must not flash the plain tier first.
                [checkinId]: { ...emptyParseActivity(), technical: getTechnicalTier() },
            }))

            // This tracker's place in line. A superseded tracker must stop
            // writing into the record and must not install a socket that
            // arrives late — `parseCheckinViaJob` opens the watch only after
            // its enqueue returns, so `onSocket` can land after the supersede.
            const myGen = (genRef.current.get(checkinId) ?? 0) + 1
            genRef.current.set(checkinId, myGen)
            const owns = () => genRef.current.get(checkinId) === myGen

            return {
                onActivity: (event) => {
                    if (!owns()) return
                    setActivities((prev) => {
                        const current = prev[checkinId]
                        if (!current) return prev
                        return { ...prev, [checkinId]: foldParseEvent(current, event) }
                    })
                },
                onStatusWords: (words) => {
                    if (!owns()) return
                    setActivities((prev) => {
                        const current = prev[checkinId]
                        if (!current) return prev
                        return {
                            ...prev,
                            [checkinId]: { ...current, statusWords: words },
                        }
                    })
                },
                onSocket: (socket) => {
                    if (!owns()) {
                        closeQuietly(socket)
                        return
                    }
                    const existing = socketsRef.current.get(checkinId)
                    if (existing && existing !== socket) closeQuietly(existing)
                    socketsRef.current.set(checkinId, socket)
                },
                end: () => {
                    if (!owns()) return
                    genRef.current.set(checkinId, myGen + 1)
                    clearSocket(checkinId)
                },
                cancel: () => {
                    if (!owns()) return
                    genRef.current.set(checkinId, myGen + 1)
                    clearSocket(checkinId)
                    setActivities((prev) => {
                        if (!(checkinId in prev)) return prev
                        const next = { ...prev }
                        delete next[checkinId]
                        return next
                    })
                },
            }
        },
        [clearSocket],
    )

    const watchJob = useCallback(
        (recordKey: number, jobId: string, scope?: string): Promise<JobResultReady> => {
            const tracker = beginParse(recordKey)
            return watchJobActivity(jobId, {
                // A parse job's Stage A answer is an event the fold ignores;
                // its status words belong on the collapsed line all the same.
                onActivity: (event) => {
                    const stageA = stageAFromEvent(event)
                    if (stageA) tracker.onStatusWords(stageA.statusWords)
                    else tracker.onActivity(event)
                },
                onSocket: tracker.onSocket,
                scope,
            }).finally(() => tracker.end())
        },
        [beginParse],
    )

    const reviewJob = useCallback(
        async (
            recordKey: number,
            jobId: string,
            scope?: string,
        ): Promise<StoredJobActivity | null> => {
            // A watch's socket, not the record's status, is what says the
            // record is live: a watch that ended on a cancel leaves a record
            // still reading `running` until the runner closes it, and that is
            // exactly the record this read is here to replace.
            if (socketsRef.current.has(recordKey)) return null
            const stored = await fetchJobActivity(jobId, scope)
            if (socketsRef.current.has(recordKey)) return null
            if (!stored.events?.length) {
                setActivities((prev) => {
                    if (!(recordKey in prev)) return prev
                    const next = { ...prev }
                    delete next[recordKey]
                    return next
                })
                return stored
            }
            const record = foldParseEvents(
                {
                    ...emptyParseActivity(),
                    technical: getTechnicalTier(),
                    review: true,
                    expanded: true,
                },
                stored.events,
            )
            setActivities((prev) => ({ ...prev, [recordKey]: record }))
            return stored
        },
        [],
    )

    // What the records currently are, for the one decision that has to be made
    // before a `setActivities` rather than inside it: a state updater must stay
    // pure, and `reviewParse` has to know whether this check-in is mid-parse.
    const activitiesRef = useRef<Record<number, ParseActivity>>(activities)
    activitiesRef.current = activities

    const reviewParse = useCallback(async (checkinId: number): Promise<boolean> => {
        // A parse running right now is its own better answer, and its socket is
        // still writing into that record; replacing it with a snapshot of the
        // previous run would fight it. The panel is already up in that case.
        const live = activitiesRef.current[checkinId]
        if (live && !live.review && live.status === 'running') return true

        const stored = await fetchParseActivity(checkinId)
        if (!stored.events?.length) return false

        const record = foldParseEvents(
            {
                ...emptyParseActivity(),
                technical: getTechnicalTier(),
                review: true,
                // Opened deliberately, so it opens open. The chevron still
                // collapses it, and the page treats that as "done reviewing".
                expanded: true,
            },
            stored.events,
        )
        setActivities((prev) => ({ ...prev, [checkinId]: record }))
        return true
    }, [])

    const clearReview = useCallback((checkinId: number) => {
        setActivities((prev) => {
            const current = prev[checkinId]
            if (!current || !current.review) return prev
            const next = { ...prev }
            delete next[checkinId]
            return next
        })
    }, [])

    const setExpanded = useCallback((checkinId: number, expanded: boolean) => {
        setActivities((prev) => {
            const current = prev[checkinId]
            if (!current) return prev
            return { ...prev, [checkinId]: { ...current, expanded } }
        })
    }, [])

    /**
     * Flip one panel's tier, and remember it for the panels that open next.
     *
     * **One record, not every record** (reported 2026-09-23). This used to
     * stamp the preference onto all of them, so opening two analyses and
     * reading the call tree of one switched the other out from under you. A
     * tier is a fact about the panel being read; the sticky preference only
     * decides what a *new* panel starts at, which `reviewParse` and
     * `beginParse` already seed from.
     */
    const setTechnical = useCallback((checkinId: number, technical: boolean) => {
        setActivities((prev) => {
            const activity = prev[checkinId]
            if (!activity || activity.technical === technical) return prev
            return { ...prev, [checkinId]: { ...activity, technical } }
        })
        setTechnicalTier(technical)
    }, [])

    // Unmount closes every socket still open. Without this a page transition
    // mid-parse leaks one per in-flight parse, and nothing would say so.
    useEffect(() => {
        const sockets = socketsRef.current
        const gens = genRef.current
        return () => {
            for (const socket of sockets.values()) closeQuietly(socket)
            sockets.clear()
            // Every tracker still holding a handle is now superseded, so a
            // late `onSocket` after unmount closes rather than reopens.
            for (const [id, gen] of gens) gens.set(id, gen + 1)
        }
    }, [])

    return {
        activities,
        beginParse,
        watchJob,
        reviewJob,
        reviewParse,
        clearReview,
        setExpanded,
        setTechnical,
    }
}
